// screens/Programme.tsx — Chunk PRG-W, plate "Programme": one programme's
// own screen. Header tiles (outcomes, budget, hours, live this week,
// waiting for your yes → Review, missed); a week grid by day with state
// words and receipts; work by owner; the guards line; a scorecard table
// with typed cells editable inline (PUT) and every other cell read-only
// with its own source word; review dates; Pause/Resume/Complete behind a
// this-does/does-not confirm card.
import { Fragment } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { BackLink } from '../components/BackLink';
import {
  ApiError,
  completeProgramme,
  getProgramme,
  getProgrammeScorecard,
  getProgrammeWeek,
  pauseProgramme,
  putProgrammeScorecardCell,
  resumeProgramme,
  type ProgrammeDetailApi,
  type ProgrammeRunApi,
  type ProgrammeScorecardApi,
  type ProgrammeState,
  type ProgrammeWeekApi,
} from '../lib/api';
import './Programme.css';

export interface ProgrammeProps {
  tenantId: string;
  id: string;
  onBack: () => void;
  onOpenReview: () => void;
}

type DetailLoad =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; detail: ProgrammeDetailApi };

type WeekLoad =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; week: ProgrammeWeekApi };

type ScorecardLoad =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; scorecard: ProgrammeScorecardApi };

type LifecycleAction = 'pause' | 'resume' | 'complete';

function mondayOf(d: Date): string {
  const copy = new Date(d);
  const day = copy.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  copy.setDate(copy.getDate() + diff);
  return copy.toISOString().slice(0, 10);
}

// The contract's Run carries no timezone of its own (only ProgrammeStepApi
// does, default 'America/Toronto' — SPEC.md's own default); every time on
// this screen is read in that zone, 24-hour, never 12-hour and never a raw
// UTC instant shown as if it were the wall clock.
const STEP_TZ = 'America/Toronto';
const TIME_FMT = new Intl.DateTimeFormat('en-CA', { timeZone: STEP_TZ, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
const DAY_FMT = new Intl.DateTimeFormat('en-US', { timeZone: STEP_TZ, weekday: 'short' });

function dayLabel(iso: string): string {
  return DAY_FMT.format(new Date(iso));
}

function timeLabel(iso: string): string {
  return TIME_FMT.format(new Date(iso));
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A human date from a YYYY-MM-DD string — "Nov 10", never "2026-11-10".
 * Parsed by hand rather than through `Date`'s own UTC-midnight reading,
 * which a browser west of UTC renders as the PREVIOUS day. */
function humanDate(isoDate: string): string {
  const [, m, d] = isoDate.split('-').map(Number);
  return `${MONTHS[(m ?? 1) - 1]} ${d ?? ''}`.trim();
}

/** "Oct 5–11" (same month) or "Oct 29 – Nov 4" (crossing one) — the week
 * heading, never a bare ISO date. */
function weekRangeLabel(startIsoDate: string): string {
  const [y, m, d] = startIsoDate.split('-').map(Number);
  const start = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 6);
  const startLabel = `${MONTHS[start.getUTCMonth()]} ${start.getUTCDate()}`;
  return start.getUTCMonth() === end.getUTCMonth()
    ? `${startLabel}–${end.getUTCDate()}`
    : `${startLabel} – ${MONTHS[end.getUTCMonth()]} ${end.getUTCDate()}`;
}

/** A run's state, in words — never a colour alone, and a missed run says so
 * rather than vanishing from the week. */
function runStateLine(run: ProgrammeRunApi): string {
  switch (run.state) {
    case 'planned':
      return 'planned';
    case 'drafting':
      return 'drafting';
    case 'waiting_yes':
      return 'waiting for your yes';
    case 'live':
      return run.receipt ?? 'live';
    case 'done':
      return run.receipt ?? `done ${run.doneAt ? timeLabel(run.doneAt) : ''}`.trim();
    case 'skipped':
      return 'skipped';
    case 'missed':
      return 'waiting on ' + (run.ownerLabel ?? run.owner);
    case 'failed':
      return run.note ?? 'failed';
    default:
      return run.state;
  }
}

function moneyLabel(cents: number, currency: string): string {
  return `${currency} ${(cents / 100).toFixed(0)}`;
}

const LIFECYCLE_COPY: Record<LifecycleAction, { label: string; does: string; doesNot: string }> = {
  pause: {
    label: 'Pause',
    does: 'Holds every standing obligation where it is — nothing new comes due.',
    doesNot: 'Does not cancel anything already waiting for your yes, and does not refund the spend so far.',
  },
  resume: {
    label: 'Resume',
    does: 'Puts the cadence back on its clock, starting from the next due obligation.',
    doesNot: 'Does not re-run anything that was missed while paused — those stay missed.',
  },
  complete: {
    label: 'Complete',
    does: 'Marks the programme done and retires its remaining steps.',
    doesNot: 'Does not delete the scorecard or the receipts — they stay on this screen.',
  },
};

export function Programme({ tenantId, id, onBack, onOpenReview }: ProgrammeProps) {
  const [detail, setDetail] = useState<DetailLoad>({ phase: 'loading' });
  const [weekStart, setWeekStart] = useState<string>(() => mondayOf(new Date()));
  const [week, setWeek] = useState<WeekLoad>({ phase: 'loading' });
  const [scorecard, setScorecard] = useState<ScorecardLoad>({ phase: 'loading' });
  const [confirming, setConfirming] = useState<LifecycleAction | null>(null);
  const [lifecycleBusy, setLifecycleBusy] = useState(false);
  const [lifecycleReceipt, setLifecycleReceipt] = useState<string | null>(null);
  const [lifecycleError, setLifecycleError] = useState<string | null>(null);
  const [cellBusy, setCellBusy] = useState<string | null>(null);
  const [cellDrafts, setCellDrafts] = useState<Record<string, string>>({});

  function readDetail() {
    setDetail({ phase: 'loading' });
    getProgramme(tenantId, id)
      .then((d) => setDetail({ phase: 'ready', detail: d }))
      .catch((err: unknown) =>
        setDetail({
          phase: 'error',
          message: err instanceof ApiError ? (err.serverMessage ?? err.message) : 'the programme could not be read',
        })
      );
  }

  function readScorecard() {
    setScorecard({ phase: 'loading' });
    getProgrammeScorecard(tenantId, id)
      .then((s) => setScorecard({ phase: 'ready', scorecard: s }))
      .catch((err: unknown) =>
        setScorecard({
          phase: 'error',
          message: err instanceof ApiError ? (err.serverMessage ?? err.message) : 'the scorecard could not be read',
        })
      );
  }

  useEffect(() => {
    readDetail();
    readScorecard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, id]);

  useEffect(() => {
    let cancelled = false;
    setWeek({ phase: 'loading' });
    getProgrammeWeek(tenantId, id, weekStart)
      .then((w) => {
        if (!cancelled) setWeek({ phase: 'ready', week: w });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setWeek({
          phase: 'error',
          message: err instanceof ApiError ? (err.serverMessage ?? err.message) : 'the week could not be read',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, id, weekStart]);

  function shiftWeek(days: number) {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + days);
    setWeekStart(mondayOf(d));
  }

  async function runLifecycle(action: LifecycleAction) {
    setLifecycleBusy(true);
    setLifecycleError(null);
    try {
      const call = action === 'pause' ? pauseProgramme : action === 'resume' ? resumeProgramme : completeProgramme;
      const { receipt } = await call(tenantId, id);
      setLifecycleReceipt(receipt);
      setConfirming(null);
      readDetail();
    } catch (err) {
      setLifecycleError(err instanceof ApiError ? (err.serverMessage ?? err.message) : 'that could not be done');
    } finally {
      setLifecycleBusy(false);
    }
  }

  async function saveCell(weekKey: string, colKey: string) {
    const cellKey = `${weekKey}:${colKey}`;
    const draft = cellDrafts[cellKey];
    if (draft === undefined) return;
    setCellBusy(cellKey);
    try {
      await putProgrammeScorecardCell(tenantId, id, weekKey, colKey, draft.trim() === '' ? null : draft);
      readScorecard();
    } catch {
      // The scorecard re-read on the next load is the honest recovery; a
      // failed save simply leaves the draft in place so nothing typed is lost.
    } finally {
      setCellBusy(null);
    }
  }

  if (detail.phase === 'loading') {
    return <div class="programme" aria-hidden="true" />;
  }
  if (detail.phase === 'error') {
    return (
      <div class="programme">
        <BackLink label="Delivery" onClick={onBack} />
        <div class="programme__say">
          The programme could not be read.
          <br />
          <span class="programme__say-soft">{detail.message}</span>
        </div>
      </div>
    );
  }

  const { programme, outcomes, tiles } = detail.detail;

  return (
    <div class="programme">
      <BackLink label="Delivery" onClick={onBack} />
      <div class="programme__header">
        <h1 class="programme__title">{programme.title}</h1>
        <span class="programme__meta">
          {programme.dayN !== null ? `day ${programme.dayN}${programme.dayTotal !== null ? ` of ${programme.dayTotal}` : ''} · ` : ''}
          {programme.state}
        </span>
      </div>

      <div class="programme__tiles">
        {outcomes.map((o) => (
          <div class="programme__tile" key={o.measure}>
            <div class="programme__tile-label">{o.measure}</div>
            <div class="programme__tile-n">
              {o.currentValue ?? '—'}
              {o.targetValue !== null ? <span class="programme__tile-of"> of {o.targetValue}</span> : null}
            </div>
            {o.typedUntil ? <div class="programme__tile-note">typed by you until {o.typedUntil}</div> : null}
          </div>
        ))}
        <div class="programme__tile">
          <div class="programme__tile-label">Budget</div>
          <div class="programme__tile-n">
            {moneyLabel(tiles.budget.spentCents, tiles.budget.currency)}
            {tiles.budget.capCents !== null ? (
              <span class="programme__tile-of"> of {moneyLabel(tiles.budget.capCents, tiles.budget.currency)}</span>
            ) : null}
          </div>
        </div>
        <div class="programme__tile">
          <div class="programme__tile-label">Your hours, this week</div>
          <div class="programme__tile-n">{tiles.hoursThisWeek ?? '—'}</div>
          <div class="programme__tile-note">from the hours ledger</div>
        </div>
        <div class="programme__tile">
          <div class="programme__tile-label">Live this week</div>
          <div class="programme__tile-n">
            {tiles.liveThisWeek.done} <span class="programme__tile-of">of {tiles.liveThisWeek.planned}</span>
          </div>
        </div>
        <button
          type="button"
          class="programme__tile programme__tile--yes"
          onClick={onOpenReview}
          disabled={tiles.waitingForYes === 0}
        >
          <div class="programme__tile-label">Waiting for your yes</div>
          <div class="programme__tile-n">{tiles.waitingForYes}</div>
        </button>
        <div class="programme__tile">
          <div class="programme__tile-label">Missed</div>
          <div class={tiles.missed > 0 ? 'programme__tile-n programme__tile-n--missed' : 'programme__tile-n'}>
            {tiles.missed}
          </div>
        </div>
      </div>

      <div class="programme__columns">
        <section class="programme__card">
          <div class="programme__card-head">
            <h2>{weekRangeLabel(weekStart)}</h2>
            <div class="programme__week-nav">
              <button type="button" onClick={() => shiftWeek(-7)}>
                ‹ previous
              </button>
              <button type="button" onClick={() => shiftWeek(7)}>
                next ›
              </button>
            </div>
          </div>
          {week.phase === 'loading' ? (
            <div aria-hidden="true" />
          ) : week.phase === 'error' ? (
            <div class="programme__say-soft">{week.message}</div>
          ) : week.week.runs.length === 0 ? (
            <div class="programme__say-soft">Nothing due this week.</div>
          ) : (
            <div class="programme__week-list">
              {week.week.runs.map((run) => (
                <div class="programme__week-row" key={run.id}>
                  <div class="programme__week-when">
                    <span class="programme__week-day">{dayLabel(run.dueAt)}</span>
                    <span class="programme__week-time">{timeLabel(run.dueAt)}</span>
                  </div>
                  <div class="programme__week-what">
                    <span class="programme__week-title">{run.title}</span>
                    <span class={`programme__week-state programme__week-state--${run.state}`}>{runStateLine(run)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <div class="programme__side">
          <section class="programme__card">
            <h2>Work by owner</h2>
            {week.phase === 'ready' && week.week.byOwner.length > 0 ? (
              <div class="programme__owner-list">
                {week.week.byOwner.map((o) => (
                  <div class="programme__owner-row" key={o.owner}>
                    <b>{o.ownerLabel}</b>
                    <span>{o.lines.join(' · ')}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div class="programme__say-soft">Nothing assigned this week.</div>
            )}
          </section>
        </div>
      </div>

      <section class="programme__card">
        <div class="programme__card-head">
          <h2>Scorecard</h2>
        </div>
        {scorecard.phase === 'loading' ? (
          <div aria-hidden="true" />
        ) : scorecard.phase === 'error' ? (
          <div class="programme__say-soft">{scorecard.message}</div>
        ) : (
          <div class="programme__scorecard" style={{ gridTemplateColumns: `1.2fr repeat(${scorecard.scorecard.columns.length}, minmax(0,1fr))` }}>
            <div class="programme__scorecard-head">Week</div>
            {scorecard.scorecard.columns.map((c) => (
              <div class="programme__scorecard-head" key={c.key}>
                {c.label}
              </div>
            ))}
            {scorecard.scorecard.rows.map((row) => (
              <Fragment key={row.weekStart}>
                <div class="programme__scorecard-week">
                  {row.weekStart}
                </div>
                {scorecard.scorecard.columns.map((c) => {
                  const cell = row.cells[c.key];
                  const cellKey = `${row.weekStart}:${c.key}`;
                  if (c.source === 'typed') {
                    const draft = cellDrafts[cellKey] ?? (cell?.value !== null && cell?.value !== undefined ? String(cell.value) : '');
                    return (
                      <div class="programme__scorecard-cell programme__scorecard-cell--typed" key={cellKey}>
                        <input
                          class="programme__scorecard-input"
                          aria-label={`${c.label}, week of ${humanDate(row.weekStart)}`}
                          placeholder="—"
                          value={draft}
                          disabled={cellBusy === cellKey}
                          onChange={(e) => setCellDrafts((cur) => ({ ...cur, [cellKey]: (e.target as HTMLInputElement).value }))}
                          onBlur={() => void saveCell(row.weekStart, c.key)}
                        />
                        <span class="programme__scorecard-source">typed</span>
                      </div>
                    );
                  }
                  return (
                    <div class="programme__scorecard-cell" key={cellKey}>
                      {cell?.value ?? '—'}
                      <span class="programme__scorecard-source">{c.source}</span>
                    </div>
                  );
                })}
              </Fragment>
            ))}
          </div>
        )}
      </section>

      {programme.reviewDates.length > 0 ? (
        <section class="programme__card">
          <h2>Review dates</h2>
          <div class="programme__review-dates">{programme.reviewDates.map(humanDate).join(' · ')}</div>
        </section>
      ) : null}

      <section class="programme__card">
        <h2>Guards</h2>
        {programme.guards.length > 0 ? <div class="programme__guards">{programme.guards.join(' · ')}</div> : null}
        {week.phase === 'ready' ? (
          <div class="programme__guards programme__guards--week">
            This week: {week.week.guards.refused} refused by the rail · {week.week.guards.promotionalReplies}{' '}
            promotional replies · spend {moneyLabel(week.week.guards.spendCents, tiles.budget.currency)}
            {week.week.guards.notes.length > 0 ? ` · ${week.week.guards.notes.join(' · ')}` : ''}
          </div>
        ) : null}
      </section>

      <div class="programme__lifecycle">
        {confirming ? (
          <div class="programme__confirm">
            <div class="programme__confirm-row">
              <b>This does:</b> {LIFECYCLE_COPY[confirming].does}
            </div>
            <div class="programme__confirm-row">
              <b>It does not:</b> {LIFECYCLE_COPY[confirming].doesNot}
            </div>
            {lifecycleError ? <div class="programme__confirm-error">{lifecycleError}</div> : null}
            <div class="programme__confirm-actions">
              <button type="button" class="programme__btn programme__btn--primary" disabled={lifecycleBusy} onClick={() => void runLifecycle(confirming)}>
                {LIFECYCLE_COPY[confirming].label} the programme
              </button>
              <button type="button" class="programme__btn" disabled={lifecycleBusy} onClick={() => setConfirming(null)}>
                Never mind
              </button>
            </div>
          </div>
        ) : (
          <div class="programme__lifecycle-actions">
            {lifecycleActionsFor(programme.state).map((action) => (
              <button type="button" class="programme__btn" key={action} onClick={() => setConfirming(action)}>
                {LIFECYCLE_COPY[action].label}
              </button>
            ))}
          </div>
        )}
        {lifecycleReceipt ? <div class="programme__receipt">{lifecycleReceipt}</div> : null}
      </div>
    </div>
  );
}

function lifecycleActionsFor(state: ProgrammeState): LifecycleAction[] {
  if (state === 'running') return ['pause', 'complete'];
  if (state === 'paused') return ['resume', 'complete'];
  return [];
}
